import * as R from 'ramda';
import { createSelector } from 'reselect';
// helpers/constants
import * as G from '../../helpers';
// feature driver-card
import { GROUP_TYPE_NONE, GROUP_TYPE_DISPATCHER, GROUP_TYPE_GROUP_AND_DISPATCHER } from './constants';
//////////////////////////////////////////////////

const selectDriversCardStore = (state) => state.driversCard;

const makeSelectFilters = () => createSelector(
  selectDriversCardStore,
  ({ filters }) => filters,
);

const makeSelectInitialFilterDays = () => createSelector(
  selectDriversCardStore,
  ({ filters }) => filters.initialFilterDays,
);

const makeSelectGroupBy = () => createSelector(
  selectDriversCardStore,
  ({ groupBy }) => groupBy,
);

const makeSelectGlobalFilterValue = () => createSelector(
  selectDriversCardStore,
  ({ globalFilterValue }) => globalFilterValue,
);

const makeSelectLoadRefFilterValue = () => createSelector(
  selectDriversCardStore,
  ({ loadRefFilterValue }) => loadRefFilterValue,
);

const makeSelectUnassignedLoads = () => createSelector(
  selectDriversCardStore,
  ({ unassignedLoads }) => unassignedLoads,
);

const makeSelectNotGroupedAssignedLoads = () => createSelector(
  selectDriversCardStore,
  ({ notGroupedAssignedLoads }) => notGroupedAssignedLoads,
);

const makeSelectNotGroupedAssignedLoadsPagination = () => createSelector(
  selectDriversCardStore,
  ({ notGroupedAssignedLoadsPagination }) => notGroupedAssignedLoadsPagination,
);

const makeSelectShowUnassignedLoads = () => createSelector(
  selectDriversCardStore,
  ({ showUnassignedLoads }) => showUnassignedLoads,
);

const makeSelectTelNotifications = () => createSelector(
  selectDriversCardStore,
  ({ telNotifications }) => telNotifications,
);

const makeSelectOpenedDriverProfile = () => createSelector(
  selectDriversCardStore,
  ({ openedDriverProfile }) => openedDriverProfile,
);

const makeSelectDriverProfileGuid = () => createSelector(
  selectDriversCardStore,
  ({ driverProfileGuid }) => driverProfileGuid,
);

const makeSelectDriverProfile = () => createSelector(
  selectDriversCardStore,
  (state) => {
    const { driverProfiles, driverProfileGuid } = state;

    const driverProfile = R.path([driverProfileGuid], driverProfiles);

    return driverProfile;
  },
);

const makeSelectZoom = () => createSelector(
  selectDriversCardStore,
  ({ zoom }) => zoom,
);

const makeSelectSearchRadiusValue = () => createSelector(
  selectDriversCardStore,
  ({ searchRadiusValue }) => searchRadiusValue,
);

const makeSelectMinEmptyHoursValue = () => createSelector(
  selectDriversCardStore,
  ({ minEmptyHoursValue }) => minEmptyHoursValue,
);

const makeSelectFilteredByPlaceAutocompleteLoads = () => createSelector(
  selectDriversCardStore,
  ({ filteredByPlaceAutocompleteLoads }) => filteredByPlaceAutocompleteLoads,
);

const makeSelectSearchPlaceAutocompleteValue = () => createSelector(
  selectDriversCardStore,
  ({ searchPlaceAutocompleteValue }) => searchPlaceAutocompleteValue,
);

const makeSelectSearchPlaceAutocompleteResult = () => createSelector(
  selectDriversCardStore,
  ({ searchPlaceAutocompleteResult }) => searchPlaceAutocompleteResult,
);

const makeSelectLoadsByDispatchGroup = () => createSelector(
  selectDriversCardStore,
  ({ loadsByDispatchGroup }) => loadsByDispatchGroup,
);

const makeSelectLoadsByDispatchGroupAndDispatchers = () => createSelector(
  selectDriversCardStore,
  ({ loadsByDispatchGroupAndDispatchers }) => loadsByDispatchGroupAndDispatchers,
);

const makeSelectDriverLoadsByDispatcher = () => createSelector(
  selectDriversCardStore,
  ({ driverLoadsByDispatcher }) => driverLoadsByDispatcher,
);

const makeSelectDriverCardsInfo = () => createSelector(
  selectDriversCardStore,
  ({ dispatchByTruck, driverCardsInfo, driverCardsByTruckInfo }) => {
    if (G.isTrue(dispatchByTruck)) return driverCardsByTruckInfo;

    return driverCardsInfo;
  },
);

const makeSelectSearchDate = () => createSelector(
  selectDriversCardStore,
  ({ searchDate }) => searchDate,
);

const makeSelectCardView = () => createSelector(
  selectDriversCardStore,
  ({ cardView }) => cardView,
);

const makeSelectCardHeight = () => createSelector(
  selectDriversCardStore,
  ({ cardHeight }) => cardHeight,
);

const makeSelectCardHeightType = () => createSelector(
  selectDriversCardStore,
  ({ cardHeightType }) => cardHeightType,
);

const makeSelectDispatchByTruck = () => createSelector(
  selectDriversCardStore,
  ({ dispatchByTruck }) => dispatchByTruck,
);

const makeSelectSearchLoadsByCustomerFilter = () => createSelector(
  selectDriversCardStore,
  ({ searchLoadsByCustomerFilter }) => searchLoadsByCustomerFilter,
);

const makeSelectOpenedGroups = () => createSelector(
  selectDriversCardStore,
  ({ openedGroups }) => openedGroups,
);

const makeSelectShowLocationName = () => createSelector(
  selectDriversCardStore,
  ({ showLocationName }) => showLocationName,
);

const makeSelectDriversCount = () => createSelector(
  selectDriversCardStore,
  ({
    groupBy,
    loadsByDispatchGroup,
    driverLoadsByDispatcher,
    filteredByPlaceAutocompleteLoads,
    notGroupedAssignedLoadsPagination,
    loadsByDispatchGroupAndDispatchers,
  }) => {
    if (R.and(G.isNotNilAndNotEmpty(groupBy), G.notEquals(groupBy, GROUP_TYPE_NONE))) {
      let list = loadsByDispatchGroup;

      if (R.equals(groupBy, GROUP_TYPE_DISPATCHER)) list = driverLoadsByDispatcher;

      if (R.equals(groupBy, GROUP_TYPE_GROUP_AND_DISPATCHER)) {
        return R.compose(
          R.length,
          R.reduce(R.concat, []),
          R.reduce(R.concat, []),
          R.reject(R.isNil),
          R.values,
          R.reject(R.isNil),
          G.mapIndexed(R.values),
        )(loadsByDispatchGroupAndDispatchers);
      }

      return R.compose(
        R.sum,
        R.map(R.length),
        R.reject(R.isNil),
        R.values,
      )(list);
    }

    if (G.isNotNilAndNotEmpty(filteredByPlaceAutocompleteLoads)) {
      return R.length(filteredByPlaceAutocompleteLoads);
    }

    return R.pathOr(0, ['totalCount'], notGroupedAssignedLoadsPagination);
  },
);

export {
  makeSelectZoom,
  makeSelectFilters,
  makeSelectGroupBy,
  makeSelectCardView,
  makeSelectCardHeight,
  makeSelectSearchDate,
  makeSelectOpenedGroups,
  makeSelectDriversCount,
  makeSelectDriverProfile,
  makeSelectCardHeightType,
  makeSelectDriverCardsInfo,
  makeSelectUnassignedLoads,
  makeSelectDispatchByTruck,
  makeSelectShowLocationName,
  makeSelectTelNotifications,
  makeSelectInitialFilterDays,
  makeSelectDriverProfileGuid,
  makeSelectGlobalFilterValue,
  makeSelectSearchRadiusValue,
  makeSelectLoadRefFilterValue,
  makeSelectMinEmptyHoursValue,
  makeSelectOpenedDriverProfile,
  makeSelectShowUnassignedLoads,
  makeSelectLoadsByDispatchGroup,
  makeSelectDriverLoadsByDispatcher,
  makeSelectNotGroupedAssignedLoads,
  makeSelectSearchLoadsByCustomerFilter,
  makeSelectSearchPlaceAutocompleteValue,
  makeSelectSearchPlaceAutocompleteResult,
  makeSelectFilteredByPlaceAutocompleteLoads,
  makeSelectNotGroupedAssignedLoadsPagination,
  makeSelectLoadsByDispatchGroupAndDispatchers,
};
